import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, FileText, Upload, Lock, CheckCircle2, XCircle, ChevronRight, Trash2, Clock, BookOpen, Timer } from "lucide-react";

const OPT_LETTERS = { "1": "A", "2": "B", "3": "C", "4": "D" };
const acceptedKeys = (q) => String(q.correct_option ?? "").split(",").map((s) => s.trim()).filter(Boolean);
const fmt = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${p(h)}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
};

export default function SIPYQPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.is_admin === true;
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("review"); // review | test | result
  const [revealed, setRevealed] = useState({});
  const [answers, setAnswers] = useState({}); // q_no -> option key
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const timerRef = useRef(null);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const durationMin = useMemo(() => {
    if (!active) return 0;
    return active.duration_minutes || Math.max(30, Math.round((active.question_count || 0) * 0.9));
  }, [active]);

  const loadPapers = async () => {
    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/si-pyq/papers`, authHeader());
      setPapers(data.papers || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { loadPapers(); }, []);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const openPaper = async (id) => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/si-pyq/paper/${id}`, authHeader());
      setActive(data); setMode("review"); setRevealed({}); setAnswers({}); setResult(null);
      clearInterval(timerRef.current);
      window.scrollTo(0, 0);
    } catch (e) { toast.error("Could not open paper"); }
  };

  const closePaper = () => { clearInterval(timerRef.current); setActive(null); setMode("review"); };

  const startTest = () => {
    if (active?.locked) { toast.error("Unlock with SI PRO to take the timed test"); return; }
    setAnswers({}); setResult(null); setMode("test");
    const secs = durationMin * 60;
    setTimeLeft(secs);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timerRef.current); submitTest(true); return 0; }
        return t - 1;
      });
    }, 1000);
    window.scrollTo(0, 0);
  };

  const submitTest = (auto = false) => {
    clearInterval(timerRef.current);
    const qs = active?.questions || [];
    let correct = 0, wrong = 0, unattempted = 0, bonus = 0;
    qs.forEach((q) => {
      const accepted = acceptedKeys(q);
      const isBonus = accepted.some((a) => a === "" || a === "0" || a.toUpperCase().startsWith("D*") || a.toUpperCase() === "DELETED");
      const sel = answers[q.q_no];
      if (isBonus) { correct++; bonus++; return; }  // deleted/no-answer → full marks to all
      if (!sel) { unattempted++; return; }
      if (accepted.includes(String(sel))) correct++;
      else wrong++;
    });
    setResult({ correct, wrong, unattempted, bonus, total: qs.length, auto });
    setMode("result");
    window.scrollTo(0, 0);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const payload = JSON.parse(await file.text());
      const { data } = await managedAxios.post(`${API_BASE}/si-pyq/admin/upload`, payload, authHeader());
      toast.success(`Uploaded: ${data.question_count} questions (${data.message})`);
      await loadPapers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid JSON or upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this paper?")) return;
    try {
      await managedAxios.delete(`${API_BASE}/si-pyq/admin/paper/${id}`, authHeader());
      toast.success("Paper deleted"); closePaper(); await loadPapers();
    } catch (e) { toast.error("Delete failed"); }
  };

  // ── Paper viewer (review / test / result) ──
  if (active) {
    const qs = active.questions || [];
    const answeredCount = Object.keys(answers).length;
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Sticky timer bar during test */}
        {mode === "test" && (
          <div className="sticky top-0 z-40 bg-slate-900 text-white shadow-lg" data-testid="pyq-timer-bar">
            <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
              <div className={`flex items-center gap-2 font-mono text-lg font-bold ${timeLeft <= 300 ? "text-red-400 animate-pulse" : ""}`} data-testid="pyq-timer">
                <Clock className="w-5 h-5" /> {fmt(timeLeft)}
              </div>
              <span className="text-xs text-slate-300">{answeredCount}/{qs.length} answered</span>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { if (window.confirm("Submit the test now?")) submitTest(false); }} data-testid="pyq-submit-test">
                Submit Test
              </Button>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <button onClick={closePaper} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-3 text-sm" data-testid="pyq-back-to-list">
              <ArrowLeft className="w-4 h-4" /> All Papers
            </button>
            <h1 className="text-xl sm:text-2xl font-bold">{active.exam}</h1>
            <p className="text-emerald-100 text-sm mt-1">
              {active.date} · {active.shift} {active.booklet_code ? `· Booklet ${active.booklet_code}` : ""} · {active.question_count} questions
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {/* Mode chooser (only in review mode) */}
          {mode === "review" && !active.locked && (
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="pyq-mode-chooser">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <BookOpen className="w-4 h-4 text-emerald-600" />
                  <span>You're in <strong>Review mode</strong> — reveal answers per question. Or attempt it like a real exam.</span>
                </div>
                <Button className="gap-2 bg-slate-900 hover:bg-slate-800" onClick={startTest} data-testid="pyq-start-test-btn">
                  <Timer className="w-4 h-4" /> Start Timed Test ({durationMin}m)
                </Button>
              </CardContent>
            </Card>
          )}

          {active.locked && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="pyq-locked-banner">
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <Lock className="w-4 h-4" /> Showing {active.preview_count} free questions. Unlock all {active.total_questions} with SI PRO.
              </div>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => navigate("/upgrade?exam=si")} data-testid="pyq-upgrade-btn">Upgrade to SI PRO</Button>
            </div>
          )}

          {/* Result summary */}
          {mode === "result" && result && (
            <Card className="border-emerald-300 shadow-md" data-testid="pyq-result-summary">
              <CardContent className="p-5">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Test Submitted{result.auto ? " (time up)" : ""}</h2>
                <p className="text-sm text-slate-500 mb-4">Answers are revealed below — your choice vs the correct answer.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg bg-emerald-50 p-3 text-center"><p className="text-2xl font-bold text-emerald-700">{result.correct}</p><p className="text-xs text-slate-500">Correct</p></div>
                  <div className="rounded-lg bg-red-50 p-3 text-center"><p className="text-2xl font-bold text-red-600">{result.wrong}</p><p className="text-xs text-slate-500">Wrong</p></div>
                  <div className="rounded-lg bg-slate-100 p-3 text-center"><p className="text-2xl font-bold text-slate-600">{result.unattempted}</p><p className="text-xs text-slate-500">Skipped</p></div>
                  <div className="rounded-lg bg-slate-900 p-3 text-center"><p className="text-2xl font-bold text-white">{result.correct}/{result.total}</p><p className="text-xs text-slate-300">Score</p></div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => { setMode("review"); setRevealed({}); }} data-testid="pyq-review-after">Review answers</Button>
                  <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={startTest} data-testid="pyq-retake">Retake test</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Questions */}
          {qs.map((q) => {
            const sel = answers[q.q_no];
            const showAnswer = mode === "result" || (mode === "review" && revealed[q.q_no]);
            return (
              <Card key={q.q_no} data-testid={`pyq-question-${q.q_no}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded px-2 py-1 shrink-0">Q{q.q_no}</span>
                    <div className="flex-1">
                      {q.topic && <span className="text-[11px] text-slate-400">{q.topic}</span>}
                      <p className="font-medium text-slate-900 text-sm sm:text-base">{q.question}</p>
                    </div>
                  </div>
                  {q.diagram_svg && (
                    <div className="mb-3 flex justify-center bg-white rounded-lg border border-slate-200 p-3 overflow-x-auto [&_svg]:max-w-full [&_svg]:h-auto"
                      data-testid={`pyq-diagram-${q.q_no}`}
                      dangerouslySetInnerHTML={{ __html: q.diagram_svg }} />
                  )}
                  <div className="space-y-2">
                    {q.options.map((o) => {
                      const isCorrect = showAnswer && acceptedKeys(q).includes(String(o.key));
                      const isSelected = String(sel) === String(o.key);
                      const isWrongSel = showAnswer && isSelected && !isCorrect;
                      let cls = "border-slate-200 bg-white text-slate-700";
                      if (isCorrect) cls = "border-emerald-400 bg-emerald-50 text-emerald-900 font-medium";
                      else if (isWrongSel) cls = "border-red-400 bg-red-50 text-red-900";
                      else if (mode === "test" && isSelected) cls = "border-slate-800 bg-slate-100 text-slate-900 font-medium";
                      const clickable = mode === "test";
                      return (
                        <div key={o.key}
                          onClick={clickable ? () => setAnswers((a) => ({ ...a, [q.q_no]: o.key })) : undefined}
                          className={`text-sm rounded-lg border px-3 py-2 flex items-start gap-2 ${cls} ${clickable ? "cursor-pointer hover:border-slate-400" : ""}`}
                          data-testid={`pyq-opt-${q.q_no}-${o.key}`}>
                          <span className="font-semibold">{OPT_LETTERS[o.key] || o.key}.</span>
                          <span>{o.text}</span>
                          {isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto shrink-0" />}
                          {isWrongSel && <XCircle className="w-4 h-4 text-red-500 ml-auto shrink-0" />}
                        </div>
                      );
                    })}
                  </div>

                  {/* Review-mode per-question reveal */}
                  {mode === "review" && (
                    !revealed[q.q_no] ? (
                      <div className="mt-3">
                        <Button variant="outline" size="sm" onClick={() => setRevealed((r) => ({ ...r, [q.q_no]: true }))} data-testid={`pyq-reveal-${q.q_no}`}>Show answer</Button>
                      </div>
                    ) : (
                      <div className="mt-3 text-sm bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid={`pyq-answer-${q.q_no}`}>
                        <p className="font-semibold text-emerald-700 mb-1">Answer: {q.correct_answer || (OPT_LETTERS[q.correct_option] || q.correct_option)}</p>
                        {q.explanation && <p className="text-slate-600">{q.explanation}</p>}
                      </div>
                    )
                  )}

                  {/* Result-mode explanation (always revealed) */}
                  {mode === "result" && (
                    <div className="mt-3 text-sm bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid={`pyq-answer-${q.q_no}`}>
                      <p className="font-semibold text-emerald-700 mb-1">
                        Answer: {q.correct_answer || (OPT_LETTERS[q.correct_option] || q.correct_option)}
                        {sel ? (acceptedKeys(q).includes(String(sel)) ? " · You got it right ✓" : ` · You chose ${OPT_LETTERS[sel] || sel} ✗`) : " · Not attempted"}
                      </p>
                      {q.explanation && <p className="text-slate-600">{q.explanation}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Bottom submit in test mode */}
          {mode === "test" && (
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-base" onClick={() => { if (window.confirm("Submit the test now?")) submitTest(false); }} data-testid="pyq-submit-test-bottom">
              Submit Test ({answeredCount}/{qs.length} answered)
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ── Paper list ──
  return (
    <div className="min-h-screen bg-slate-50" data-testid="si-pyq-page">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-3 text-sm" data-testid="pyq-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><FileText className="w-6 h-6" /></div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">SI Previous Year Papers</h1>
              <p className="text-emerald-100 text-sm">TS &amp; AP Police SI exams · review or timed attempt</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {isAdmin && (
          <Card className="mb-5 border-dashed border-2 border-emerald-300 bg-emerald-50/40" data-testid="pyq-admin-upload">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-slate-700">
                <p className="font-semibold text-slate-900">Admin: Upload exam paper</p>
                <p className="text-xs text-slate-500">Upload a paper JSON file (exam, date, shift, questions[]).</p>
              </div>
              <input ref={fileRef} type="file" accept="application/json,.json" onChange={handleUpload} className="hidden" data-testid="pyq-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="pyq-upload-btn">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload JSON"}
              </Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-center text-slate-500 py-16">Loading papers…</p>
        ) : papers.length === 0 ? (
          <Card data-testid="pyq-empty"><CardContent className="py-16 text-center text-slate-500">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No papers uploaded yet.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {papers.map((p) => (
              <Card key={p.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openPaper(p.id)} data-testid={`pyq-paper-${p.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 text-sm sm:text-base">{p.exam}</p>
                    <p className="text-xs text-slate-500 mt-1">{p.date} · {p.shift} · {p.question_count} questions
                      {p.paper_type ? <Badge variant="outline" className="ml-2 text-[10px]">{p.paper_type}</Badge> : null}
                    </p>
                  </div>
                  {isAdmin && <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="text-slate-300 hover:text-red-500 p-1" data-testid={`pyq-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
